import { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import { alpha, styled } from "@mui/material/styles";
import LottiePlayer from "react-lottie-player";
import animationData from "./toggleAnimation";

// consume `theme.radii.pill` + `theme.elevation('low')` so the
// switch matches the global token language. The Lottie animation itself is
// untouched; only the surrounding chrome is rebuilt on tokens.
const ToggleWrapper = styled(Box)(({ theme }) => ({
  display: "block",
  lineHeight: 0,
  borderRadius: theme.radii?.pill ?? 999,
  border: `1px solid ${alpha(theme.palette.mode === "light" ? "#94a3b8" : "#cbd5e1", theme.palette.mode === "light" ? 0.45 : 0.24)}`,
  backgroundColor:
    theme.palette.mode === "light" ? alpha("#ffffff", 0.72) : alpha("#020617", 0.38),
  overflow: "hidden",
  boxShadow: typeof theme.elevation === "function" ? theme.elevation("low") : "none",
  cursor: "pointer",
  padding: 0,
  borderWidth: 1,
  appearance: "none",
  WebkitAppearance: "none",
  MozAppearance: "none"
}));

const TogglePlayer = styled(LottiePlayer)(() => ({
  margin: 0,
  outline: "none",
  alignItems: "center",
  justifyContent: "center",
  pointerEvents: "none"
}));

function parseUnit(value) {
  const stringValue = `${value}`;
  const match = stringValue.match(/[\d.\-+]*\s*(.*)/);
  const num = parseFloat(stringValue);
  return [isNaN(num) ? 0 : num, match?.[1] || "px"];
}

const lightSegment = [5, 50];
const darkSegment = [50, 95];

export default function ThemeModeSwitch({ isDarkMode, onChange, size = 65, speed = 1.3 }) {
  const [segments, setSegments] = useState([0, 0]);
  const [goTo, setGoTo] = useState(isDarkMode ? darkSegment[0] : lightSegment[0]);
  const [play, setPlay] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setSegments(isDarkMode ? lightSegment : darkSegment);
    setPlay(hasLoaded);
  }, [isDarkMode, hasLoaded]);

  const handleToggle = () => {
    setGoTo(null);
    setSegments(isDarkMode ? darkSegment : lightSegment);
    setPlay(true);
    onChange(!isDarkMode);
  };

  const handleLoad = () => {
    setHasLoaded(true);
  };

  const [sizeNumber, sizeUnit] = parseUnit(size);

  return (
    <ToggleWrapper
      component="button"
      type="button"
      onClick={handleToggle}
      aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
      aria-pressed={isDarkMode}
      sx={{
        width: `${sizeNumber}${sizeUnit}`,
        height: `${sizeNumber * 0.5}${sizeUnit}`
      }}
    >
      <TogglePlayer
        play={play}
        speed={speed}
        loop={false}
        animationData={animationData}
        goTo={goTo}
        segments={segments}
        onLoad={handleLoad}
        style={{
          display: "flex",
          opacity: hasLoaded ? 1 : 0,
          transition: hasLoaded ? "opacity 0.25s ease" : "none",
          marginTop: `${sizeNumber * -0.575}${sizeUnit}`,
          marginLeft: `${sizeNumber * -0.32}${sizeUnit}`,
          width: `${sizeNumber * 1.65}${sizeUnit}`,
          height: `${sizeNumber * 1.65}${sizeUnit}`
        }}
      />
    </ToggleWrapper>
  );
}
